import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RestoreStateFromMFA } from '@rd/core/mfa/store/actions/mfa.actions';
import {
  MFAAutoRedemption,
  MFAOneTimeRedemption,
  MFAOutcome,
  MFAProductType,
  MFARedemptionType,
  MFAShoppingCart,
  OneTimeRedemptionEverydaySpend,
} from '@rd/core/mfa/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, filter, map, of, switchMap, tap } from 'rxjs';
import { EverydaySpendActions } from '../actions/everyday-spend.actions';

@Injectable()
export class EverydaySpendMfaEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);

  restoreStateFromMFA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RestoreStateFromMFA),
      filter(({ outcome }) => outcome === MFAOutcome.Success),
      map(({ data }) => data),
      switchMap((data) => {
        const actions = [];

        if (this.isMFADataEverydaySpend(data)) {
          actions.push(
            EverydaySpendActions.hydrateRedemption({
              hydrationData: data,
              outcome: MFAOutcome.Success,
            }),
          );
        }

        return actions;
      }),
    ),
  );

  hydrateEverydaySpendRedemption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EverydaySpendActions.hydrateRedemption),
      filter(({ outcome }) => outcome === MFAOutcome.Success),
      switchMap(({ hydrationData }) => {
        const { loyaltyProgramId, sourceAmount, targetAmount, membershipId } =
          hydrationData.data;

        return of(
          EverydaySpendActions.submitPointsTransfer({
            loyaltyProgramId,
            formData: {
              sourceAmount,
              targetAmount,
              membershipId,
            },
          }),
        );
      }),
    ),
  );

  redirectToEverydaySpendRedeemPage$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(RestoreStateFromMFA)),
        this.actions$.pipe(ofType(EverydaySpendActions.hydrateRedemption)),
      ]).pipe(
        filter((values) => values.every(isNotNull)),
        filter(([restoreState]) => restoreState.outcome === MFAOutcome.Success),
        tap(([restoreState]) => {
          this.router.navigateByUrl(restoreState.data.verifiedRedirectUrl);
        }),
      ),
    { dispatch: false },
  );

  private isMFADataEverydaySpend(
    data: MFAAutoRedemption | MFAOneTimeRedemption | MFAShoppingCart,
  ): data is OneTimeRedemptionEverydaySpend {
    return (
      data.type === MFARedemptionType.OneTimeRedemtion &&
      data.product === MFAProductType.EverydaySpend
    );
  }
}
